import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBadge, defaultNumberFormat } from "../../components/index.ts";
import type { PostureByCodeAndTier } from "./use-posture.ts";
import {
  deriveByCodeRows,
  postureKindLabel,
  findingCodeLabel,
  riskTierLabel,
  riskTierStatus,
  riskTierTitle,
  riskTierProminent,
  byCodeTierHref,
  byCodeHref,
  RISK_TIER_ORDER,
  type ByCodeRow,
} from "./vocab.ts";

/**
 * NHI findings by code and tier (task 11.10; design 12.2 "NHI findings by code and tier"; 12.3
 * drill-down; 12.4 semantic table + risk tier by icon+text, not colour alone).
 *
 * Reads the findings page's authoritative `by_code_and_tier` aggregates and renders them as a
 * two-dimensional grid: one ROW per observed finding code (labelled with its posture kind) and one
 * COLUMN per risk tier, highest-severity tier FIRST so the most serious findings are surfaced
 * prominently (design 12.2). `deriveByCodeRows` orders the rows by their worst non-zero tier, so a
 * code with any critical/high findings sorts to the top.
 *
 * RISK TIER IS CONVEYED WITHOUT RELYING ON COLOUR (design 12.4) in several redundant ways:
 *   - each tier COLUMN header is a `StatusBadge` — a glyph + a text word (Critical/High/…), so the
 *     tier reads as icon+text, with colour only a fourth redundant channel;
 *   - the two highest tiers (critical, high) are marked `data-prominent` so CSS adds a non-colour
 *     emphasis (a left rule / heavier weight), making them stand out without hue;
 *   - rows are SORTED by worst tier so severity is also conveyed by position.
 *
 * Every non-zero count is a drill-down button to the events behind that code/tier
 * (`category=posture`, code, risk_tier); the row total drills to all events for the code. A zero
 * cell is plain text (nothing to drill into). The grid is a real semantic `<table>` with a caption,
 * column headers and a row header per code.
 */

export interface FindingsByCodeAndTierProps {
  readonly aggregates: readonly PostureByCodeAndTier[];
}

export function FindingsByCodeAndTier(props: FindingsByCodeAndTierProps): ReactNode {
  const navigate = useNavigate();
  const rows = deriveByCodeRows(props.aggregates);

  return (
    <section className="pst-section" aria-label="NHI findings by code and tier">
      <div className="pst-section__head">
        <h2 className="pst-section__title">Findings by code and tier</h2>
        <p className="pst-section__note">
          One row per finding code, one column per risk tier. Higher tiers are listed first and
          emphasised; a count drills to the events behind it.
        </p>
      </div>

      <div className="pst-grid__scroll">
        <table className="data-table data-table--dense" data-sticky="true">
          <caption className="sr-only">
            Non-human-identity and host findings counted by finding code and risk tier, highest tier
            first
          </caption>
          <thead>
            <tr>
              <th scope="col">Finding</th>
              <th scope="col">Kind</th>
              {RISK_TIER_ORDER.map((tier) => (
                <th
                  key={tier}
                  scope="col"
                  data-align="right"
                  data-prominent={riskTierProminent(tier) ? "true" : undefined}
                >
                  <StatusBadge
                    level={riskTierStatus(tier)}
                    label={riskTierLabel(tier)}
                    title={riskTierTitle(tier)}
                    size="sm"
                  />
                </th>
              ))}
              <th scope="col" data-align="right">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="data-table__empty-row">
                <td colSpan={RISK_TIER_ORDER.length + 3}>
                  <span className="data-table__empty">
                    No NHI or host findings were reported in this window. Widen the time range, or
                    check that the AIS is reporting posture findings.
                  </span>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <CodeRow key={row.code} row={row} onDrill={(href) => navigate(href)} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CodeRow(props: { row: ByCodeRow; onDrill: (href: string) => void }): ReactNode {
  const { row } = props;
  return (
    <tr data-prominent={row.worstTier !== null && riskTierProminent(row.worstTier) ? "true" : undefined}>
      <th scope="row" data-align="left">
        {findingCodeLabel(row.code)}
      </th>
      <td data-align="left">
        <span className="pst-faint">{postureKindLabel(row.kind)}</span>
      </td>

      {RISK_TIER_ORDER.map((tier) => {
        const count = row.countsByTier[tier];
        return (
          <td
            key={tier}
            data-align="right"
            data-prominent={riskTierProminent(tier) ? "true" : undefined}
          >
            {count === 0 ? (
              <span className="pst-zero" aria-label="none">
                n/a
              </span>
            ) : (
              <button
                type="button"
                className="pst-link pst-num"
                onClick={() => props.onDrill(byCodeTierHref(row.code, tier))}
                aria-label={`Open the ${defaultNumberFormat(count)} ${riskTierLabel(
                  tier,
                ).toLowerCase()}-tier events for ${findingCodeLabel(row.code).toLowerCase()}`}
              >
                {defaultNumberFormat(count)}
              </button>
            )}
          </td>
        );
      })}

      <td data-align="right">
        <button
          type="button"
          className="pst-link pst-num pst-num--strong"
          onClick={() => props.onDrill(byCodeHref(row.code))}
          aria-label={`Open all ${defaultNumberFormat(row.total)} events for ${findingCodeLabel(
            row.code,
          ).toLowerCase()}`}
        >
          {defaultNumberFormat(row.total)}
        </button>
      </td>
    </tr>
  );
}
